import api from '../../utils/api';

// 1. Storage Summary Interface
export interface StorageSummaryData {
  totalEvents: number;
  totalUploadAttempts: number;
  successfulUploads: number;
  successfulDeletes: number;
  failedEvents: number;
  totalUploadBytes: number;
  totalDeletedBytes: number;
  netUploadBytes: number;
  netUploadBytesReadable?: string;
  totalFilesUploaded: number;
  uploadSuccessRate: number;
  avgUploadLatencyMs: number;
  p95UploadLatencyMs: number;
}

export interface StorageSummaryResponse {
  success: boolean;
  data: StorageSummaryData;
}

// 2. Storage Actors Breakdown Interface
export interface StorageActor {
  actorId: string;
  actorType: string;
  actorName: string | null;
  actorPhone: string | null;
  actorEmail: string | null;
  vendorType: string | null;
  storageBytes: number;
  storageBytesReadable?: string;
  filesCount: number;
  successfulUploads: number;
  failedEvents: number;
  avgUploadLatencyMs: number;
  p95UploadLatencyMs: number;
  lastEventTs: string;
}

export interface StorageActorsResponse {
  success: boolean;
  data: {
    startDate: string;
    endDate: string;
    filters: {
      actorType: string;
      vendorType: string | null;
      search: string | null;
      sortBy: string;
    };
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    items: StorageActor[];
  };
}

// 3. Storage Type Analysis Interface
export interface EntityTypeBreakdownItem {
  entityType: string;
  filesCount: number;
  totalBytes: number;
  totalBytesReadable?: string;
  percentage: number;
}

export interface MimeTypeBreakdownItem {
  mimeType: string;
  filesCount: number;
  totalBytes: number;
  totalBytesReadable?: string;
  percentage: number;
}

export interface FileExtBreakdownItem {
  fileExt: string;
  filesCount: number;
  totalBytes: number;
  totalBytesReadable?: string;
  percentage: number;
}

export interface StorageTypesResponse {
  success: boolean;
  data: {
    entityTypeBreakdown: EntityTypeBreakdownItem[];
    mimeTypeBreakdown: MimeTypeBreakdownItem[];
    fileExtBreakdown: FileExtBreakdownItem[];
  };
}

// 4. Storage Health Metrics Interface
export interface PerformanceTimeSeriesPoint {
  day: string;
  avgUploadLatencyMs: number;
  successfulUploads: number;
  failedEvents: number;
}

export interface ErrorDistributionItem {
  errorCode: string;
  errorMessage: string;
  occurrences: number;
}

export interface RecentFailureItem {
  eventId: string;
  actorId: string;
  actorType: string;
  entityType: string;
  originalFileName: string;
  operation: string;
  errorCode: string;
  errorMessage: string;
  eventTs: string;
}

export interface StorageHealthResponse {
  success: boolean;
  data: {
    daily: PerformanceTimeSeriesPoint[];
    errorDistribution: ErrorDistributionItem[];
    recentFailures: RecentFailureItem[];
  };
}

// 5. Storage Actor Drilldown Interface
export interface ActorFileItem {
  eventId: string;
  originalFileName: string;
  downloadUrl: string;
  entityType: string;
  mimeType: string;
  fileSizeBytes: number;
  fileSizeBytesReadable?: string;
  uploadedAt: string;
}

export interface ActorDrilldownData {
  actorId: string;
  actorType: string;
  name: string | null;
  totalBytesUsed: number;
  totalBytesUsedReadable?: string;
  maxStorageLimitBytes: number;
  maxStorageLimitBytesReadable?: string;
  files: ActorFileItem[];
}

export interface ActorDrilldownResponse {
  success: boolean;
  data: ActorDrilldownData;
}

// API functions
export async function fetchStorageSummary(params?: {
  actorType?: 'VENDOR' | 'USER';
  startDate?: string;
  endDate?: string;
}): Promise<StorageSummaryResponse> {
  const response = await api.get('/master-admin/storage/summary', { params });
  return response.data;
}

export async function fetchStorageActors(params?: {
  actorType?: 'VENDOR' | 'USER';
  vendorType?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  startDate?: string;
  endDate?: string;
}): Promise<StorageActorsResponse> {
  const response = await api.get('/master-admin/storage/actors', { params });
  return response.data;
}

export async function fetchStorageTypes(params?: {
  actorType?: 'VENDOR' | 'USER';
  startDate?: string;
  endDate?: string;
}): Promise<StorageTypesResponse> {
  const response = await api.get('/master-admin/storage/types', { params });
  return response.data;
}

export async function fetchStorageHealth(params?: {
  actorType?: 'VENDOR' | 'USER';
  startDate?: string;
  endDate?: string;
}): Promise<StorageHealthResponse> {
  const response = await api.get('/master-admin/storage/health', { params });
  return response.data;
}

export async function fetchStorageActorDrilldown(
  actorId: string,
  params?: {
    actorType?: 'VENDOR' | 'USER';
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }
): Promise<ActorDrilldownResponse> {
  const response = await api.get(`/master-admin/storage/actors/${encodeURIComponent(actorId)}`, { params });
  return response.data;
}
